import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/shop_logo.png';
import { SHOP_NAME } from "../Variables.js";

Modal.setAppElement('#root');

// Formatters (same as PaymentTracking)
const formatAmount = (value) => `₦${Number(value || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
const formatAmount1 = (value) => `N${Number(value || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
const dateFormatter = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const formatDisplayDate = (date) => date ? dateFormatter.format(new Date(date)) : 'N/A';
const formatDateTime = (date) => date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }).replace(',', '');
const formatDateForFilename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const IncomeTracking = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [incomeRecords, setIncomeRecords] = useState([]);
  const [filteredIncomeRecords, setFilteredIncomeRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [incomeSources] = useState(['Product Sales', 'Service Revenue', 'Other']);
  const [invoices, setInvoices] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [newIncome, setNewIncome] = useState({
    invoiceId: '',
    amount: 0,
    source: 'Product Sales',
    date: new Date().toISOString().split('T')[0],
    bankAccountId: '',
    description: ''
  });
  const [editingIncome, setEditingIncome] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [isFetchingIncome, setIsFetchingIncome] = useState(true);
  const [hasFetchedIncome, setHasFetchedIncome] = useState(false);

  // PDF Date Range
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const incomeCollectionRef = collection(db, "incomeRecords");
  const invoicesCollectionRef = collection(db, "invoices");
  const accountsCollectionRef = collection(db, "accounts");
  const customersCollectionRef = collection(db, "customers");

  useEffect(() => {
    if (user && userData) {
      fetchCustomers();
      fetchInvoices();
      fetchBankAccounts();
      fetchIncomeRecords();
    }
  }, [user, userData]);

  // SEARCH: description OR source
  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = incomeRecords.filter((income) =>
      (income.description || '').toLowerCase().includes(lowerSearch) ||
      income.source.toLowerCase().includes(lowerSearch)
    );
    setFilteredIncomeRecords(filtered);
  }, [searchTerm, incomeRecords]);

  const fetchCustomers = async () => {
    if (!user?.uid) return;
    try {
      const q = query(customersCollectionRef, where("active", "==", true));
      const data = await getDocs(q);
      setCustomers(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      setError("Failed to fetch customers"); setShowModal(true);
    }
  };

  const fetchInvoices = async () => {
    if (!user?.uid) return;
    try {
      const q = query(invoicesCollectionRef);
      const data = await getDocs(q);
      setInvoices(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      setError("Failed to fetch invoices"); setShowModal(true);
    }
  };

  const fetchBankAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef, where("type", "==", "Asset"));
      const data = await getDocs(q);
      setBankAccounts(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      setError("Failed to fetch bank accounts"); setShowModal(true);
    }
  };

  const fetchIncomeRecords = async () => {
    if (!user?.uid) return;
    setIsFetchingIncome(true);
    try {
      const q = query(incomeCollectionRef);
      const data = await getDocs(q);
      const incomeList = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setIncomeRecords(incomeList);
      setHasFetchedIncome(true);
    } catch (error) {
      setError("Failed to fetch income records"); setShowModal(true);
    } finally {
      setIsFetchingIncome(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewIncome(prev => ({ ...prev, [name]: value }));
  };

  const handleAddIncome = async () => {
    if (newIncome.amount <= 0 || !newIncome.date || !newIncome.source) {
      setError("Positive amount, date, and source are required"); setShowModal(true); return;
    }
    setUpdate(true);
    try {
      await addDoc(incomeCollectionRef, {
        ...newIncome,
        userId: user?.uid,
        amount: parseFloat(newIncome.amount),
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Income recorded successfully");
      fetchIncomeRecords();
      resetNewIncome();
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to record income"); console.error(error);
    } finally {
      setUpdate(false); setShowModal(true);
    }
  };

  const handleUpdateIncome = async () => {
    setUpdate(true);
    try {
      const incomeDoc = doc(db, "incomeRecords", editingIncome.id);
      await updateDoc(incomeDoc, {
        ...newIncome,
        amount: parseFloat(newIncome.amount)
      });
      setSuccessMessage("Income updated successfully");
      fetchIncomeRecords();
      resetNewIncome();
      setEditingIncome(null);
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update income"); console.error(error);
    } finally {
      setUpdate(false); setShowModal(true);
    }
  };

  const resetNewIncome = () => {
    setNewIncome({
      invoiceId: '',
      amount: 0,
      source: 'Product Sales',
      date: new Date().toISOString().split('T')[0],
      bankAccountId: '',
      description: ''
    });
  };

  const handleEditIncome = (incomeToEdit) => {
    setEditingIncome(incomeToEdit);
    setNewIncome({
      invoiceId: incomeToEdit.invoiceId || '',
      amount: incomeToEdit.amount,
      source: incomeToEdit.source,
      date: incomeToEdit.date,
      bankAccountId: incomeToEdit.bankAccountId || '',
      description: incomeToEdit.description || ''
    });
    setIsEditModalOpen(true);
  };

  // PDF GENERATION (Same as PaymentTracking)
  const validateDateRange = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates"); setShowModal(true); return false;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date cannot be after end date"); setShowModal(true); return false;
    }
    return true;
  };

  const generateIncomePDF = () => {
    if (!validateDateRange()) return;

    const filteredByDate = incomeRecords.filter(income => {
      const incomeDate = new Date(income.date);
      return incomeDate >= new Date(startDate) && incomeDate <= new Date(endDate);
    });

    if (filteredByDate.length === 0) {
      setError("No income records found in selected date range"); setShowModal(true); return;
    }

    const doc = new jsPDF();
    const fullName = userData?.firstName && userData?.lastName ? `${userData.firstName} ${userData.lastName}` : user?.displayName || "Unknown";

    let y = 10;
    doc.addImage(Logo, "PNG", 14, y, 15, 15);
    doc.setFontSize(16); doc.setFont("helvetica", "bold");
    doc.text(SHOP_NAME, 35, y + 5);
    doc.setFontSize(16); doc.setFont("helvetica", "normal");
    doc.text("Income Report", 35, y + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, y + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, y + 23);
    doc.text(`Period: ${dateFormatter.format(new Date(startDate))} to ${dateFormatter.format(new Date(endDate))}`, 35, y + 28);
    y += 33;

    const sorted = [...filteredByDate].sort((a, b) => new Date(a.date) - new Date(b.date));
    const tableData = sorted.map((inc, i) => {
      const invoice = invoices.find(i => i.id === inc.invoiceId);
      const customer = invoice ? customers.find(c => c.id === invoice.customerId) : null;
      return [
        (i + 1).toString(),
        formatDisplayDate(inc.date),
        inc.source,
        inc.description || '-',
        invoice ? `${invoice.invoiceNumber} - ${customer?.name || 'N/A'}` : '-',
        bankAccounts.find(a => a.id === inc.bankAccountId)?.name || '-',
        formatAmount1(inc.amount)
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [['S/N', 'Date', 'Source', 'Description', 'Invoice/Customer', 'Bank Account', 'Amount']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: 5, right: 0 },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 25 },
        2: { cellWidth: 30 },
        3: { cellWidth: 40 },
        4: { cellWidth: 40 },
        5: { cellWidth: 30 },
        6: { cellWidth: 25 }
      }
    });

    const total = sorted.reduce((sum, inc) => sum + parseFloat(inc.amount), 0);
    doc.setFontSize(12);
    doc.text(`Total Income: ${formatAmount1(total)}`, 14, doc.lastAutoTable.finalY + 10);

    doc.save(`income_report_${formatDateForFilename(new Date(startDate))}_to_${formatDateForFilename(new Date(endDate))}.pdf`);
    setIsDateRangeModalOpen(false);
    setStartDate(''); setEndDate('');
  };

  if (loading) return <Loader text="Loading" />;
  if (!userData) return <Loader text="Reading" />;

  const allowedRoles = ['Cashier', 'Accountant', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingIncome && !hasFetchedIncome) return <p className="loading-text">Loading income records...</p>;
    if (filteredIncomeRecords.length === 0) {
      return searchTerm
        ? <p className="loading-text">No income records match your search.</p>
        : <p className="loading-text">No income records found. Add your first income record.</p>;
    }

    return filteredIncomeRecords.map((incomeItem) => {
      const invoice = invoices.find(i => i.id === incomeItem.invoiceId);
      const customer = invoice ? customers.find(c => c.id === invoice.customerId) : null;
      return (
        <div key={incomeItem.id} className="class-item">
          <div className="column gap-1">
            <p>
              <strong>{incomeItem.description || 'Income'}</strong><br />
              <span>Amount: {formatAmount(incomeItem.amount)}</span><br />
              <span>Source: {incomeItem.source}</span><br />
              <small className="font-light-blue-cash bold">{formatDisplayDate(incomeItem.date)}</small><br />
              {invoice && <span>Invoice: {invoice.invoiceNumber} - {customer?.name || 'N/A'}</span>}
              {incomeItem.bankAccountId && <span><br />Bank: {bankAccounts.find(a => a.id === incomeItem.bankAccountId)?.name || 'Unknown'}</span>}
            </p>
          </div>
          <div className="row align-center justify-center gap-15">
            <button className="list-btn" onClick={() => handleEditIncome(incomeItem)}>
              <i className="fas fa-edit"></i>
            </button>
          </div>
        </div>
      );
    });
  };

  return (
    <>
      {update && <Loader text="Processing" />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link"><i className="fa-solid fa-arrow-left size3"></i></Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Income Tracking</h2>
            <Link to="#" className="home-link" onClick={fetchIncomeRecords}><i className="fa-solid fa-rotate-right size4"></i></Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <div className="row justify-center align-center gap-15 add-buttons margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Income</button>
              <button className="login-button login-button-d caps" onClick={() => setIsDateRangeModalOpen(true)}>Download</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by description or source"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => { setError(""); setSuccessMessage(""); setShowModal(false); }}
        />
      )}

      {/* Add Income Modal */}
      <Modal isOpen={isAddModalOpen} onRequestClose={() => setIsAddModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Income</h2>
        <form className="session-form modal-scroll">
          <label className="input-label">Invoice (Optional)</label>
          <select name="invoiceId" className="input-field" value={newIncome.invoiceId} onChange={handleInputChange}>
            <option value="">None</option>
            {invoices.map(invoice => (
              <option key={invoice.id} value={invoice.id}>
                {invoice.invoiceNumber} - {customers.find(c => c.id === invoice.customerId)?.name || 'Unknown'}
              </option>
            ))}
          </select>

          <label className="input-label">Amount</label>
          <input type="number" step="0.01" name="amount" className="input-field" placeholder="0.00" value={newIncome.amount} onChange={handleInputChange} required />

          <label className="input-label">Source</label>
          <select name="source" className="input-field" value={newIncome.source} onChange={handleInputChange} required>
            {incomeSources.map(source => <option key={source} value={source}>{source}</option>)}
          </select>

          <label className="input-label">Date</label>
          <input type="date" name="date" className="input-field" value={newIncome.date} onChange={handleInputChange} required />

          <label className="input-label">Bank Account (Optional)</label>
          <select name="bankAccountId" className="input-field" value={newIncome.bankAccountId} onChange={handleInputChange}>
            <option value="">None</option>
            {bankAccounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
          </select>

          <label className="input-label">Description</label>
          <textarea name="description" className="input-field" rows="3" placeholder="Enter description..." value={newIncome.description} onChange={handleInputChange} />

          <div className="row justify-center gap-1 mt-20">
            <button type="button" className="login-button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button type="button" className="login-button" onClick={handleAddIncome}>Add Income</button>
          </div>
        </form>
      </Modal>

      {/* Edit Income Modal */}
      <Modal isOpen={isEditModalOpen} onRequestClose={() => { setIsEditModalOpen(false); resetNewIncome(); setEditingIncome(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Income</h2>
        <form className="session-form modal-scroll">
          <label className="input-label">Invoice (Optional)</label>
          <select name="invoiceId" className="input-field" value={newIncome.invoiceId} onChange={handleInputChange}>
            <option value="">None</option>
            {invoices.map(invoice => (
              <option key={invoice.id} value={invoice.id}>
                {invoice.invoiceNumber} - {customers.find(c => c.id === invoice.customerId)?.name || 'Unknown'}
              </option>
            ))}
          </select>

          <label className="input-label">Amount</label>
          <input type="number" step="0.01" name="amount" className="input-field" value={newIncome.amount} onChange={handleInputChange} required />

          <label className="input-label">Source</label>
          <select name="source" className="input-field" value={newIncome.source} onChange={handleInputChange} required>
            {incomeSources.map(source => <option key={source} value={source}>{source}</option>)}
          </select>

          <label className="input-label">Date</label>
          <input type="date" name="date" className="input-field" value={newIncome.date} onChange={handleInputChange} required />

          <label className="input-label">Bank Account (Optional)</label>
          <select name="bankAccountId" className="input-field" value={newIncome.bankAccountId} onChange={handleInputChange}>
            <option value="">None</option>
            {bankAccounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
          </select>

          <label className="input-label">Description</label>
          <textarea name="description" className="input-field" rows="3" value={newIncome.description} onChange={handleInputChange} />

          <div className="row justify-center gap-1 mt-20">
            <button type="button" className="login-button" onClick={() => { setIsEditModalOpen(false); resetNewIncome(); setEditingIncome(null); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleUpdateIncome}>Update Income</button>
          </div>
        </form>
      </Modal>

      {/* Date Range Modal for PDF */}
      <Modal isOpen={isDateRangeModalOpen} onRequestClose={() => { setIsDateRangeModalOpen(false); setStartDate(''); setEndDate(''); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Select Date Range for Income Report</h2>
        <form className="session-form">
          <label className="input-label">Start Date</label>
          <input type="date" className="input-field" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          <label className="input-label">End Date</label>
          <input type="date" className="input-field" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
          <div className="row justify-center gap-1 mt-20">
            <button type="button" className="login-button" onClick={() => { setIsDateRangeModalOpen(false); setStartDate(''); setEndDate(''); }}>Cancel</button>
            <button type="button" className="login-button" onClick={generateIncomePDF}>Generate PDF</button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default IncomeTracking;